<template>
  <div class="photo-gallery">
    <!-- 載入中狀態 -->
    <div v-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
      <p class="mt-4 grey--text">載入圖片中...</p>
    </div>

    <!-- 圖片網格 -->
    <div v-else-if="fixImageList.length > 0">
      <v-row>
        <v-col v-for="(image, index) in fixImageList" :key="index" cols="12" sm="6" md="4" lg="3">
          <v-card class="image-card" elevation="2">
            <!-- 圖片容器 -->
            <div class="image-container" @click="isShowOpenImageDialogBtn(image, index)">
              <img
                :src="getImageSrc(image)"
                :alt="getImageName(index)"
                class="gallery-image"
                @error="handleImageError"
              />

              <!-- 懸浮操作按鈕 -->
              <div class="image-overlay">
                <v-btn
                  fab
                  small
                  color="error"
                  class="ma-1"
                  @click.stop="isShowDeletePhotoDialogBtn(index)"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </div>
            </div>

            <!-- 圖片資訊 -->
            <v-card-text class="pa-3">
              <div class="text-body-2 text-truncate">
                {{ getImageName(index) }}
              </div>
              <div class="text-caption grey--text">第 {{ index + 1 }} 張圖片</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- 圖片預覽 Dialog -->
    <v-dialog v-model="isShowOpenPhotoDialog" max-width="90vw" max-height="90vh" persistent>
      <v-card>
        <v-card-title class="pa-2">
          <span>{{ selectedImageName }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeOpenImageDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-0">
          <div class="dialog-image-container">
            <img :src="selectedImageSrc" :alt="selectedImageName" class="dialog-image" />
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="error" @click="closeOpenImageDialog">
            {{ $t('common_close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 刪除確認 Dialog -->
    <v-dialog v-model="isShowDeletePhotoDialog" max-width="400" persistent>
      <v-card>
        <v-card-title class="headline">
          <v-icon color="warning" class="mr-2">mdi-alert</v-icon>
          {{ $t('common_delete') }}
        </v-card-title>

        <v-card-text>
          您確定要刪除這張圖片嗎？
          <br />
          <strong>{{ deleteImageName }}</strong>
          <br />
          <span class="error--text">此操作無法復原。</span>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="success" @click="closeDeletePhotoDialog">
            {{ $t('common_cancel') }}
          </v-btn>
          <v-btn color="error" text @click="confirmDeletePhoto">
            {{ $t('common_delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { HttpFix } from '@/js/http-Manager'
export default {
  props: {
    editedFix: {
      type: Object,
      required: true
    },
    fixTab: {
      type: String
    }
  },
  name: 'PhotoGallery',
  data () {
    return {
      loading: false,
      fixImageList: [],

      // 圖片預覽 Dialog
      isShowOpenPhotoDialog: false,
      selectedImageSrc: '',
      selectedImageName: '',
      selectedImageIndex: -1,

      // 刪除確認 Dialog
      isShowDeletePhotoDialog: false,
      deleteImageIndex: -1,
      deleteImageName: ''
    }
  },

  async mounted () {
    await this.getPhoto()
  },

  watch: {
    async fixTab (val) {
      if (val === 'tab-2') {
        await this.getPhoto()
      }
    }
  },

  methods: {
    async deletePhoto (index) {
      try {
        await HttpFix.DeleteImage({
          fixNumber: this.editedFix.Number,
          fileName: this.editedFix.FileNameList[index]
        })

        this.editedFix.FileNameList.splice(index, 1)
        await HttpFix.UpdateFix(this.editedFix)
      } catch (error) {
        console.error('刪除圖片失敗:', error)
      }
    },
    async searchFix () {
      await this.$emit('searchFix', this.editedFix)
    },
    async getPhoto () {
      if (this.editedFix.FileNameList && this.editedFix.FileNameList.length > 0) {
        try {
          this.loading = true

          let response = await HttpFix.GetImage({
            fixNumber: this.editedFix.Number,
            fixFileList: this.editedFix.FileNameList.join(',')
          })

          this.fixImageList = response.status === 200 ? response.data.Data : []
        } catch (error) {
          console.error('獲取圖片失敗:', error)
          this.fixImageList = []
        } finally {
          this.loading = false
        }
      } else {
        this.fixImageList = []
        this.loading = false
      }
    },

    // 圖片預覽相關
    isShowOpenImageDialogBtn (image, index) {
      this.selectedImageSrc = this.getImageSrc(image)
      this.selectedImageName = this.getImageName(index)
      this.selectedImageIndex = index
      this.isShowOpenPhotoDialog = true
    },
    async closeOpenImageDialog () {
      this.isShowOpenPhotoDialog = false
      this.selectedImageSrc = ''
      this.selectedImageName = ''
      this.selectedImageIndex = -1
    },

    // 刪除確認相關
    isShowDeletePhotoDialogBtn (index) {
      this.deleteImageIndex = index
      this.deleteImageName = this.getImageName(index)
      this.isShowDeletePhotoDialog = true
    },

    async confirmDeletePhoto () {
      await this.deletePhoto(this.deleteImageIndex)
      this.closeDeletePhotoDialog()
    },

    async closeDeletePhotoDialog () {
      this.isShowDeletePhotoDialog = false
      this.deleteImageIndex = -1
      this.deleteImageName = ''
      await this.getPhoto()
      await this.searchFix()
    },

    // 工具方法
    getImageSrc (image) {
      // 根據您的 API 回傳格式調整
      return image.url || image.src || image
    },

    getImageName (index) {
      return this.editedFix.FileNameList[index] || `圖片 ${index + 1}`
    },

    handleImageError (event) {
      event.target.src = '/placeholder-image.png' // 設定預設圖片
    }
  }
}
</script>

<style scoped>
.photo-gallery {
  padding: 16px;
}

.image-card {
  position: relative;
  transition: transform 0.2s ease-in-out;
}

.image-card:hover {
  transform: translateY(-4px);
}

.image-container {
  position: relative;
  height: 200px;
  overflow: hidden;
  cursor: pointer;
}

.gallery-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.image-container:hover .gallery-image {
  transform: scale(1.05);
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.image-container:hover .image-overlay {
  opacity: 1;
}

.dialog-image-container {
  display: flex;
  justify-content: center;
  align-items: center;
  max-height: 70vh;
  overflow: auto;
}

.dialog-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
